import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './PolicyPage.css';
import './HelpPage.css';

const HelpPage = () => {
    const navigate = useNavigate();

    return (
        <div className="policy-page">
            <button className="policy-back-btn" onClick={() => navigate(-1)}>
                ← Back
            </button>

            <div className="policy-container help-container">
                <h1>Getting Into VerDict AI</h1>
                <p className="policy-meta">Signing up and signing back in &nbsp;·&nbsp; VerDict AI Prototype</p>
                <p>
                    Everything a new tester needs to create an account or sign back in — including the small speed
                    bumps you'll actually hit along the way.
                </p>

                <h2>Creating a new account</h2>
                <ol className="help-steps">
                    <li>
                        <strong>Open the app</strong> at <Link to="/login">/login</Link>. It opens on the sign-in
                        screen by default — switch to the <strong>Create account</strong> tab.
                    </li>
                    <li>
                        <strong>Fill in your name, email, and password.</strong> All three are required — there's no
                        separate username, so your email is how you'll sign back in later. Pick a password that
                        isn't a known one: we check it against breached-password lists and reject a match with
                        “Password has been found in an online data breach. For account safety, please use a
                        different password.”
                        <div className="help-mock" aria-hidden="true">
                            <div className="help-mock-tabs">
                                <span>Sign In</span>
                                <span className="help-mock-active">Create account</span>
                            </div>
                            <div className="help-mock-field"><label>Full name</label><div className="help-mock-box">Jane Doe</div></div>
                            <div className="help-mock-field"><label>Email</label><div className="help-mock-box">you@example.com</div></div>
                            <div className="help-mock-field"><label>Password</label><div className="help-mock-box">••••••••</div></div>
                            <div className="help-mock-btn">Create account</div>
                        </div>
                    </li>
                    <li>
                        <strong>Tap Create account.</strong> The button reads “Please wait…” briefly while the
                        account is created.
                    </li>
                    <li>
                        <strong>Verify your email.</strong> A 6-digit code is sent to the address you signed up
                        with — enter it and tap <strong>Verify &amp; continue</strong>. The code expires in 10
                        minutes; a resend link unlocks a few seconds after the screen appears.
                        <div className="help-mock" aria-hidden="true">
                            <div className="help-mock-field"><label>Sign-in code</label></div>
                            <div className="help-mock-codes">
                                <div className="help-mock-box">4</div>
                                <div className="help-mock-box">8</div>
                                <div className="help-mock-box">2</div>
                                <div className="help-mock-box">9</div>
                                <div className="help-mock-box">1</div>
                                <div className="help-mock-box">6</div>
                            </div>
                            <div className="help-mock-btn" style={{ marginTop: 12 }}>Verify &amp; continue</div>
                        </div>
                    </li>
                    <li>
                        <strong>Choose a plan.</strong> Verifying drops you on a subscription screen, not straight
                        into the app. Basic is $5.99/mo (up to 100 queries) and Premium is $10.99/mo (up to 300
                        queries), both billed annually by default — both start with a 15-day free trial, and in this
                        prototype build your card is never actually charged once the trial ends.
                        <div className="help-mock-plans" aria-hidden="true">
                            <div className="help-mock-plan">
                                <div className="help-mock-plan-name">Basic</div>
                                <div className="help-mock-plan-price">$5.99/mo</div>
                                <div className="help-mock-plan-desc">Up to 100 queries</div>
                                <div className="help-mock-btn">Start 15-day free trial</div>
                            </div>
                            <div className="help-mock-plan">
                                <div className="help-mock-plan-name">Premium</div>
                                <div className="help-mock-plan-price">$10.99/mo</div>
                                <div className="help-mock-plan-desc">Up to 300 queries</div>
                                <div className="help-mock-btn">Start 15-day free trial</div>
                            </div>
                        </div>
                    </li>
                    <li>
                        <strong>Complete checkout with a test card.</strong> Tapping <strong>Start 15-day free
                        trial</strong> opens a Checkout panel. This build runs in Stripe's Development mode, so
                        instead of a real card, use the <strong>Pay with test card</strong> button it offers — it
                        fills in a standard test Visa number and submits it. No real card is charged; “Total due
                        today” reads $0.00.
                        <div className="help-mock-pay" aria-hidden="true">
                            <div className="help-mock-test-btn">Pay with test card</div>
                            <div className="help-mock-field"><label>Card number</label><div className="help-mock-box">4242 4242 4242 4242</div></div>
                            <div className="help-mock-pay-total"><span>Total due today</span><strong>$0.00</strong></div>
                        </div>
                    </li>
                    <li>
                        <strong>Land in the app.</strong> Checkout confirms with “Trial successfully started!”,
                        showing the trial end date and the test card on file (Visa ···4242). Tap
                        <strong> Continue</strong> and you're dropped into the actual product.
                    </li>
                </ol>

                <div className="help-callout">
                    <strong>Faster path:</strong> tap <strong>Continue with Google</strong> on either tab to sign up
                    or sign in with your Google account in one click. It creates the account the first time you use
                    it, skipping the breach check and email code entirely.
                </div>

                <h2>Signing back in</h2>
                <ol className="help-steps">
                    <li>
                        <strong>Open the app</strong> at <Link to="/login">/login</Link> — the
                        <strong> Sign In</strong> tab is selected by default.
                        <div className="help-mock" aria-hidden="true">
                            <div className="help-mock-tabs">
                                <span className="help-mock-active">Sign In</span>
                                <span>Create account</span>
                            </div>
                            <div className="help-mock-field"><label>Email</label><div className="help-mock-box">you@example.com</div></div>
                            <div className="help-mock-field"><label>Password</label><div className="help-mock-box">••••••••</div></div>
                            <div className="help-mock-links"><span>Email me a code instead</span><span>Forgot password?</span></div>
                            <div className="help-mock-btn">Sign In</div>
                        </div>
                    </li>
                    <li>
                        <strong>Enter your email and password</strong>, then tap <strong>Sign In</strong>. If your
                        account hasn't started a plan yet, you'll land on the same subscription screen described at
                        the end of the sign-up flow — that's expected, not an error.
                    </li>
                </ol>

                <p>A few alternatives are available next to the password field:</p>
                <ul>
                    <li><strong>Email me a code instead</strong> — enter your email first (it needs the email in the box before it'll send anything), then use the 6-digit code sent to it instead of your password. Only works for an email that already has an account.</li>
                    <li><strong>Forgot password?</strong> — sends a reset code to your email; enter it along with a new password.</li>
                    <li><strong>Continue with Google</strong> — same button as sign-up; signs you straight into your existing account if the email matches.</li>
                </ul>

                <h2>Common messages</h2>
                <ul>
                    <li><strong>“Couldn't find your account.”</strong> — that email hasn't signed up yet; use Create account instead.</li>
                    <li><strong>“That email address is taken.”</strong> — an account already exists; use Sign In instead.</li>
                    <li><strong>“Password has been found in an online data breach.”</strong> — choose a different password.</li>
                    <li><strong>“Enter your email first, then request a code.”</strong> — the email box was empty when you tapped “Email me a code instead.”</li>
                </ul>

                <h2>Still stuck?</h2>
                <p>
                    Email us at <a href="mailto:contact@nerdailytics.com">contact@nerdailytics.com</a> and we'll help you
                    get in.
                </p>
            </div>
        </div>
    );
};

export default HelpPage;
